import React, { useState } from "react"
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
} from "@mui/material"
import {
  Delete,
  Assistant,
  Close,
  CollectionsOutlined,
} from "@mui/icons-material"
import DropzoneImage from "../../../../components/DropzoneImage"

const CharacteristicCard = ({
  characteristic,
  inspectionNumber,
  readOnly,
  description,
  onDescriptionChange,
  onImageUpload,
  onImageDelete,
  onBack,
  onNext,
  isFirst,
  isLast,
  onSaveDescription,
}) => {
  const [lightboxImage, setLightboxImage] = useState(null)

  if (!characteristic) return null

  const images = characteristic.images || []
  const hasImages = images.length > 0

  return (
    <Card>
      <CardHeader
        title={`${inspectionNumber ? inspectionNumber + " - " : ""}${characteristic.name}`}
        action={
          <Chip
            color="secondary"
            variant="outlined"
            size="small"
            icon={<CollectionsOutlined fontSize="small" />}
            label={`${images.length} image${images.length !== 1 ? "ns" : "m"}`}
          />
        }
      />
      <Divider sx={{ mb: 2 }} />
      <CardContent sx={{ px: 4 }}>
        {/* Existing images gallery */}
        {hasImages && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 3,
            }}
          >
            {images.map((img) => (
              <Box
                key={img.id}
                sx={{
                  position: "relative",
                  width: 120,
                  height: 120,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  cursor: "pointer",
                  "&:hover .delete-btn": {
                    opacity: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src={img.image}
                  alt={characteristic.name}
                  onClick={() => setLightboxImage(img.image)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                {!readOnly && (
                  <IconButton
                    className="delete-btn"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      onImageDelete(img.id)
                    }}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      bgcolor: "rgba(0,0,0,0.6)",
                      color: "white",
                      opacity: 0,
                      transition: "opacity 0.2s",
                      "&:hover": {
                        bgcolor: "error.main",
                      },
                      p: 0.3,
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Upload zone */}
        {!readOnly && (
          <DropzoneImage
            onUploadComplete={onImageUpload}
            characteristicId={characteristic.id}
            apiEndpoint="/property-inspection/characteristic/images/"
          />
        )}

        {/* Description */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <TextField
            placeholder="Escreva sobre a vistoria deste cômodo..."
            sx={{
              width: "100%",
              backgroundColor: "background.neutral",
              borderRadius: "10px",
              "& .MuiInputBase-input::placeholder": {
                color: "gray",
                opacity: 1,
              },
            }}
            multiline
            value={description || ""}
            onChange={(e) => onDescriptionChange(e.target.value)}
            maxRows={25}
            minRows={3}
            variant="standard"
            disabled={readOnly}
            InputProps={{
              endAdornment: !readOnly && (
                <InputAdornment position="end">
                  <Tooltip title="A descrição será gerada pela IA ao clicar em 'Gerar Descrição IA'">
                    <span>
                      <IconButton disabled={!hasImages}>
                        <Assistant
                          color={hasImages ? "secondary" : "disabled"}
                          fontSize="large"
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
              disableUnderline: true,
              sx: {
                padding: "1rem",
                border: "none",
                outline: "none",
              },
            }}
          />
        </Box>
      </CardContent>

      <Divider />
      <CardActions sx={{ justifyContent: "space-between", px: 2, py: 1.5 }}>
        <Button disabled={isFirst} onClick={onBack}>
          Voltar
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {!readOnly && onSaveDescription && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onSaveDescription(characteristic.id, description)}
            >
              Salvar Descrição
            </Button>
          )}
          <Button variant="text" onClick={onNext}>
            {isLast ? "Finalizar" : "Próximo"}
          </Button>
        </Box>
      </CardActions>

      {/* Lightbox dialog */}
      <Dialog
        open={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 0 }}>
          {lightboxImage && (
            <Box
              component="img"
              src={lightboxImage}
              alt="Imagem da vistoria"
              sx={{ maxWidth: "100%", maxHeight: "80vh", display: "block" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  )
}

export default CharacteristicCard
